import React, { useEffect, useMemo, useState } from 'react';
import { teacherApi } from '../services/api';

// Phase 8 — only teachers can mark a student's plan/goals as completed.
// Phase 10 — `mode="page"` renders this inline as a full sub-view of the
// teacher's class flow instead of as a centered modal. The user reported
// that the modal felt grafted on; a real page makes the toggles feel like
// a first-class part of the teacher panel.
//
// Backed by /teacher/student-program/:uuid (read) plus the two new toggle
// endpoints under /entries/:id/complete and /goals/:id/complete.

const F = { fontFamily: 'Inter' } as const;
const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

interface ScheduleEntry {
  id: string | number;
  day_of_week: number;
  start_time?: string | null;
  end_time?: string | null;
  title?: string | null;
  is_completed?: boolean;
  subjects?: { name?: string } | null;
}
interface Goal {
  id: string | number;
  target_value?: number;
  achieved_value?: number;
  goal_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: string;
  // T19 — student-created vs teacher-assigned + the goal's window.
  created_by_type?: string;
  period_type?: string;
  period_start?: string | null;
  period_end?: string | null;
  goal_definitions?: { title?: string } | null;
  subject_units?: { name?: string } | null;
}

const PERIOD_LABEL: Record<string, string> = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık' };

const fmtTime = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
};

const TeacherStudentProgramModal: React.FC<{
  studentUuid: string;
  studentName: string;
  onClose: () => void;
  // Phase 10: page mode renders inline (no backdrop, no centered card)
  // and skips the body-scroll-lock effect. Default 'modal' preserves the
  // pre-Phase 10 popup behaviour for any caller that still wants it.
  mode?: 'modal' | 'page';
}> = ({ studentUuid, studentName, onClose, mode = 'modal' }) => {
  const [schedules, setSchedules] = useState<Array<{ id: string; study_schedule_entries: ScheduleEntry[] }>>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());

  const reload = () => {
    setLoading(true);
    teacherApi.studentProgram(studentUuid)
      .then((r) => {
        setSchedules((r.data?.schedule ?? []) as any);
        setGoals((r.data?.goals ?? []) as Goal[]);
      })
      .catch((e: any) => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [studentUuid]);

  useEffect(() => {
    if (mode === 'page') return; // page mode keeps the underlying scroll
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, [mode]);

  const allEntries = useMemo(
    () => schedules.flatMap((s) => s.study_schedule_entries ?? []),
    [schedules]
  );
  const entriesByDay = useMemo(() => {
    const out: Record<number, ScheduleEntry[]> = {};
    for (const e of allEntries) {
      const d = e.day_of_week;
      (out[d] = out[d] ?? []).push(e);
    }
    return out;
  }, [allEntries]);

  const toggleEntry = async (entry: ScheduleEntry) => {
    const key = `e-${entry.id}`;
    setPending((p) => new Set(p).add(key));
    setError(null);
    const next = !entry.is_completed;
    try {
      await teacherApi.setScheduleEntryCompletion(studentUuid, entry.id, next);
      // Optimistic patch — update the row in place rather than reloading the
      // whole program every click. Reload again from server in the background
      // so DB truth wins on edge cases (e.g. another tab toggled).
      setSchedules((prev) => prev.map((s) => ({
        ...s,
        study_schedule_entries: s.study_schedule_entries.map((e) => (
          String(e.id) === String(entry.id) ? { ...e, is_completed: next } : e
        )),
      })));
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
      reload();
    } finally {
      setPending((p) => { const n = new Set(p); n.delete(key); return n; });
    }
  };

  const toggleGoal = async (goal: Goal) => {
    const key = `g-${goal.id}`;
    setPending((p) => new Set(p).add(key));
    setError(null);
    const next = goal.status !== 'completed';
    try {
      await teacherApi.setGoalCompletion(studentUuid, goal.id, next);
      setGoals((prev) => prev.map((g) => (
        String(g.id) === String(goal.id) ? { ...g, status: next ? 'completed' : 'active' } : g
      )));
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
      reload();
    } finally {
      setPending((p) => { const n = new Set(p); n.delete(key); return n; });
    }
  };

  // Bigger, more obvious check toggle — the previous 28px circle was too
  // subtle on a busy modal; teachers were missing it entirely. 36px circle
  // with a thick coloured ring + an explicit "✓ Tamamlandı" label when
  // active makes the affordance impossible to miss.
  const checkButton = (isCompleted: boolean, isPending: boolean, onClick: () => void) => (
    <button
      onClick={onClick}
      disabled={isPending}
      title={isCompleted ? 'Tamamlandı işaretini kaldır' : 'Tamamlandı olarak işaretle'}
      className="flex items-center gap-2 flex-shrink-0 disabled:opacity-50 hover:opacity-90 transition-all rounded-full"
      style={{
        padding: '4px 12px 4px 4px',
        background: isCompleted ? '#11D600' : '#ffffff',
        border: `2.5px solid ${isCompleted ? '#0DA600' : '#3973bf'}`,
        boxShadow: '0px 2px 6px rgba(0,0,0,0.18)',
        cursor: isPending ? 'wait' : 'pointer',
      }}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center"
        style={{ background: isCompleted ? '#ffffff' : '#3973bf' }}
      >
        {isCompleted ? (
          <svg width="14" height="11" viewBox="0 0 10 8" fill="none" stroke="#0DA600" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 4l3 3 5-6" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round">
            <path d="M5 12h14M12 5v14" />
          </svg>
        )}
      </span>
      <span style={{ ...F, fontSize: 12, fontWeight: 700, color: isCompleted ? '#ffffff' : '#3973bf', whiteSpace: 'nowrap' }}>
        {isPending ? '…' : isCompleted ? 'Tamamlandı' : 'İşaretle'}
      </span>
    </button>
  );

  // Page mode skips the modal-shell. Otherwise the centered popup wrapper
  // stays so any caller still using the old modal pattern keeps working.
  const isPage = mode === 'page';
  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    isPage ? (
      <div>{children}</div>
    ) : (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="absolute inset-0 bg-black bg-opacity-40" />
        <div
          className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[860px] max-h-[90vh] overflow-y-auto p-8"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    );

  return (
    <Wrapper>
      <div className={isPage ? '' : 'relative'}>
        {!isPage && (
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-[44px] h-[44px] rounded-full flex items-center justify-center hover:bg-gray-200"
            aria-label="Kapat"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
              <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
            </svg>
          </button>
        )}

        <p style={{ ...F, fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Çalışma Planı + Hedefler
        </p>
        <h2 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b', marginBottom: 4 }}>
          {studentName}
        </h2>
        <p style={{ ...F, fontSize: 12, color: '#666', marginBottom: 16 }}>
          Yeşil tikleri buradan açıp kapatabilirsiniz. Öğrenci ve velinin programında tik aynen görünecektir.
        </p>

        {error && (
          <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>{error}</div>
        )}

        {loading ? (
          <p className="text-center text-gray-400 py-10" style={F}>Yükleniyor…</p>
        ) : (
          <>
            <h3 style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b', marginBottom: 8 }}>Haftalık Plan</h3>
            <div className="bg-white rounded-[14px] p-4 mb-6 space-y-3" style={{ border: '1px solid #e5e5e5' }}>
              {allEntries.length === 0 ? (
                <p className="text-center text-gray-400 text-[13px] py-4" style={F}>Plan kaydı yok.</p>
              ) : (
                Array.from({ length: 7 }, (_, dow) => entriesByDay[dow] ?? []).map((entries, dow) => {
                  if (entries.length === 0) return null;
                  return (
                    <div key={dow}>
                      <p style={{ ...F, fontSize: 12, fontWeight: 700, color: '#3973bf', marginBottom: 4 }}>{DAY_NAMES[dow]}</p>
                      <div className="space-y-1.5">
                        {entries.map((entry) => {
                          const k = `e-${entry.id}`;
                          return (
                            <div key={String(entry.id)} className="flex items-center gap-3 px-3 py-2 rounded-[10px]" style={{ background: entry.is_completed ? '#e8f9e6' : '#fafafa' }}>
                              {checkButton(!!entry.is_completed, pending.has(k), () => toggleEntry(entry))}
                              <div className="flex-1">
                                <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>
                                  {entry.title || entry.subjects?.name || 'Plan'}
                                </p>
                                <p style={{ ...F, fontSize: 11, color: '#888' }}>
                                  {fmtTime(entry.start_time)} – {fmtTime(entry.end_time)}
                                  {entry.subjects?.name && ` · ${entry.subjects.name}`}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <h3 style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b', marginBottom: 8 }}>Hedefler</h3>
            <div className="bg-white rounded-[14px] p-4 space-y-2" style={{ border: '1px solid #e5e5e5' }}>
              {goals.length === 0 ? (
                <p className="text-center text-gray-400 text-[13px] py-4" style={F}>Hedef yok.</p>
              ) : (
                goals.map((g) => {
                  const k = `g-${g.id}`;
                  const completed = g.status === 'completed';
                  const label = g.goal_definitions?.title ?? g.subject_units?.name ?? 'Hedef';
                  return (
                    <div key={String(g.id)} className="flex items-center gap-3 px-3 py-2 rounded-[10px]" style={{ background: completed ? '#e8f9e6' : '#fafafa' }}>
                      {checkButton(completed, pending.has(k), () => toggleGoal(g))}
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{label}</p>
                          {/* T19 — a student's own goal must be recognisable
                              next to teacher-assigned ones. */}
                          <span
                            className="inline-block rounded-full px-2 py-0.5"
                            style={{ ...F, fontSize: 9, fontWeight: 700, color: '#fff', background: g.created_by_type === 'student' ? '#e8930c' : '#e5599b' }}
                          >
                            {g.created_by_type === 'student' ? 'Öğrenci Hedefi' : 'Öğretmen Hedefi'}
                          </span>
                          {(g.period_type === 'weekly' || g.period_type === 'monthly') && (
                            <span className="inline-block rounded-full px-2 py-0.5" style={{ ...F, fontSize: 9, fontWeight: 700, color: '#fff', background: '#3973bf' }}>
                              {PERIOD_LABEL[g.period_type]}
                            </span>
                          )}
                        </div>
                        <p style={{ ...F, fontSize: 11, color: '#888' }}>
                          {(() => {
                            const fmtD = (v?: string | null) => {
                              if (!v) return '';
                              const d = new Date(v);
                              return isNaN(d.getTime()) ? '' : d.toLocaleDateString('tr-TR', { timeZone: 'UTC' });
                            };
                            const s = fmtD(g.period_start ?? g.goal_date);
                            const e = fmtD(g.period_end ?? g.goal_date);
                            return (s && e && s !== e ? `${s} – ${e}` : s) || '—';
                          })()}
                          {(g.start_time || g.end_time) && ` · ${fmtTime(g.start_time)} – ${fmtTime(g.end_time)}`}
                          {' · '}
                          {(g.achieved_value ?? 0)}/{g.target_value ?? 0}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default TeacherStudentProgramModal;
